import { StarIcon } from "@heroicons/react/20/solid";
import { NoteEntityId, NoteId } from "@sp-crm/core";
import { IEntityNote } from "components/notes/edit";
import { ImportantNoteEditor } from "components/notes/important-note-editor";
import { EditButton } from "components/ui/action-button";
import { ClampedText } from "components/ui/clamped-text";
import {
    GetNotesForEntityQuery,
    UpdateNoteMutationVariables,
    useGetNotesForEntityQuery,
    useUpdateNoteMutation,
} from "generated/graphql";
import { produce } from "immer";
import React from "react";
import { useQueryClient } from "react-query";
import { breakNewlines } from "util/text";

interface ImportantNoteBannerProps {
    entityId: NoteEntityId;
}

export const ImportantNoteBanner: React.FC<ImportantNoteBannerProps> = props => {
    const { entityId } = props;
    const query = useGetNotesForEntityQuery({ entityId });
    const queryClient = useQueryClient();
    const updateMutation = useUpdateNoteMutation();
    const [isEditing, setIsEditing] = React.useState(false);

    const pinnedNote = query.data?.getNotesForEntity.find(n => n.isPinned && !n.isDraft);

    const onEditNote = React.useCallback(
        async (noteId: NoteId, text: string, isPinned: boolean) => {
            const payload: UpdateNoteMutationVariables = {
                entityId,
                noteId,
                params: {
                    text,
                    isPublished: true,
                    isPinned,
                },
            };
            const result = await updateMutation.mutateAsync(payload);
            queryClient.setQueryData<GetNotesForEntityQuery>(
                ["getNotesForEntity", { entityId }],
                data => {
                    if (!data) return data;
                    return produce(data, draft => {
                        draft.getNotesForEntity = draft.getNotesForEntity.map(n =>
                            n._id === result.updateNote._id ? result.updateNote : n,
                        );
                    });
                },
            );
            setIsEditing(false);
        },
        [entityId, updateMutation, queryClient],
    );

    if (!pinnedNote) {
        return null;
    }

    const editableNote: IEntityNote = {
        _id: pinnedNote._id,
        authorId: pinnedNote.authorId,
        text: pinnedNote.text,
        dateAdded: new Date(pinnedNote.dateAdded),
        dateAddedManualOverride: new Date(pinnedNote.dateAdded),
        wasEdited: pinnedNote.wasEdited,
        isPinned: pinnedNote.isPinned,
        editorId: pinnedNote.editorId,
        updatedAt: new Date(pinnedNote.updatedAt),
    };

    return (
        <div className="px-4 pb-3">
            <div className="rounded-md bg-brand-100 ring-1 ring-inset ring-brand-200 p-4">
                <div className="flex">
                    <div className="shrink-0">
                        <StarIcon aria-hidden="true" className="size-5 text-brand-500" />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                        <div className="flex items-start justify-between space-x-2">
                            <h3 className="text-sm font-medium text-brand-800">
                                Important Note
                            </h3>
                            {isEditing ? null : (
                                <EditButton
                                    backgroundColor="bg-gray-100"
                                    onClick={() => setIsEditing(true)}
                                />
                            )}
                        </div>
                        {isEditing ? (
                            <ImportantNoteEditor
                                note={editableNote}
                                onEditNote={onEditNote}
                                leaveEditMode={() => setIsEditing(false)}
                            />
                        ) : (
                            <div className="mt-1 text-sm text-brand-900">
                                <ClampedText lines={3}>
                                    {breakNewlines(pinnedNote.text)}
                                </ClampedText>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
